import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { RELIC_DEFS } from "../data/balance.js";
import {
  CONTENT_PANEL_STYLE,
  FONT_FAMILY,
  PRIMARY_BUTTON_STYLE,
  RARITY_COLOR,
  setButtonEnabled,
  TYPO,
} from "./uiTokens.js";
import { relicEffectText, relicLevelText } from "./relicLabels.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { relicIconKey } from "../themes/imageKeys.js";

/** 팝업 표시 상태 — relicId + 현재 누적 개수 + 구매 가능 여부. */
export interface RelicDetailData {
  readonly relicId: string;
  readonly count: number;
  readonly affordable: boolean;
}

export interface RelicDetailPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  /** 획득 버튼 — 골드 차감 후 1개 획득(레벨 +1). 호출자가 update로 갱신. */
  readonly onAcquire: (relicId: string) => void;
  readonly onClose: () => void;
}

/**
 * 유물 상세 팝업 — ModalLayer 기반 (CheatModal/SettingsModal 패턴).
 * 이름·등급색 + 효과(현재 레벨, effectExpr×level) + 레벨/진행 {count}/{count+1} + 설명 + 획득 버튼.
 * 획득 시 호출자(GameScene)가 acquire 후 update()로 레벨·효과·버튼을 갱신한다.
 */
export class RelicDetailPopup extends ModalLayer {
  private readonly title: Phaser.GameObjects.Text;
  private readonly icon: Phaser.GameObjects.Image;
  private readonly effectText: Phaser.GameObjects.Text;
  private readonly levelText: Phaser.GameObjects.Text;
  private readonly descText: Phaser.GameObjects.Text;
  private readonly acquireBtn: NineSliceButton;
  private readonly opts: RelicDetailPopupOptions;
  private relicId = "";

  constructor(scene: Phaser.Scene, opts: RelicDetailPopupOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onClose(),
    });
    this.opts = opts;

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const pw = 300;
    const ph = 264;
    // dim 클릭 닫기 — 패널 영역은 제외(패널 내부 클릭은 안 닫힘).
    this.setBackdropCloseExclusion(cx - pw / 2, cy - ph / 2, pw, ph);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: pw,
      height: ph,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    this.title = scene.add
      .text(cx, cy - ph / 2 + 30, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.title, color: PALETTE.textBright })
      .setOrigin(0.5);
    this.icon = scene.add
      .image(cx, cy - ph / 2 + 70, "icon_tab_relic")
      .setOrigin(0.5)
      .setDisplaySize(40, 40);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, pw, 18, getTextDirection()), cy - ph / 2 + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => this.opts.onClose());
    this.effectText = scene.add
      .text(cx, cy - 28, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.subtitle, color: PALETTE.textSecondary })
      .setOrigin(0.5);
    this.levelText = scene.add
      .text(cx, cy + 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.positiveSoft })
      .setOrigin(0.5);
    this.descText = scene.add
      .text(cx, cy + 38, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textDim,
        align: "center",
        wordWrap: { width: pw - 48 },
      })
      .setOrigin(0.5);
    this.acquireBtn = new NineSliceButton(scene, cx, cy + ph / 2 - 36, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("relic.acquire"),
      widthOverride: 170,
      heightOverride: 40,
    });
    this.acquireBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      if (this.relicId) this.opts.onAcquire(this.relicId);
    });

    this.contentContainer.add([
      this.title,
      this.icon,
      closeBtn,
      this.effectText,
      this.levelText,
      this.descText,
      this.acquireBtn,
    ]);
  }

  /** 표시 갱신 (열 때 + 획득 후). */
  update(data: RelicDetailData): void {
    this.relicId = data.relicId;
    const def = RELIC_DEFS[data.relicId];
    if (!def) return;
    this.title.setText(t(`relic.name.${def.relicId}`)).setColor(RARITY_COLOR[def.grade]);
    this.icon.setTexture(relicIconKey(def.relicId) ?? "icon_tab_relic");
    this.effectText.setText(relicEffectText(def, data.count));
    this.levelText.setText(relicLevelText(data.count));
    this.descText.setText(t(`relic.desc.${def.relicId}`));
    this.acquireBtn.setLabel(t("relic.acquireCost", { cost: def.cost }));
    setButtonEnabled(this.acquireBtn, data.affordable);
  }
}
